import { useEffect, useState, type FormEvent } from 'react';
import {
  TASK_STATUS,
  TASK_STATUS_LABELS,
  type Task,
  type TaskInput,
  type TaskStatus,
} from '@stromo/shared';
import { Modal } from './Modal';
import { useUsersLookup } from '../lib/lookups';

interface TaskEditModalProps {
  task: Task | null;
  onClose: () => void;
  onSave: (patch: Partial<TaskInput>) => void;
  onDelete?: (id: number) => void;
  saving?: boolean;
  /** Read-Only Mode (z.B. Rolle Mitarbeiter): Modal zeigt Felder als
   *  reine Anzeige, ohne Speichern/Löschen. */
  readOnly?: boolean;
}

interface Draft {
  title: string;
  status: TaskStatus;
  progress_percent: number;
  schedule_start: string;
  schedule_end: string;
  internal_start: string;
  internal_end: string;
  site_manager_id: number | null;
  subcontractor: string;
  notes: string;
}

const emptyDraft = (): Draft => ({
  title: '',
  status: 'open',
  progress_percent: 0,
  schedule_start: '',
  schedule_end: '',
  internal_start: '',
  internal_end: '',
  site_manager_id: null,
  subcontractor: '',
  notes: '',
});

function taskToDraft(task: Task): Draft {
  return {
    title: task.title,
    status: task.status,
    progress_percent: task.progress_percent,
    schedule_start: task.schedule_start ?? '',
    schedule_end: task.schedule_end ?? '',
    internal_start: task.internal_start ?? '',
    internal_end: task.internal_end ?? '',
    site_manager_id: task.site_manager_id,
    subcontractor: task.subcontractor ?? '',
    notes: task.notes ?? '',
  };
}

function draftToPatch(d: Draft, original: Task): Partial<TaskInput> {
  const trim = (s: string): string | null => (s.trim() === '' ? null : s.trim());
  const patch: Partial<TaskInput> = {};
  if (d.title.trim() !== original.title) patch.title = d.title.trim();
  if (d.status !== original.status) patch.status = d.status;
  if (d.progress_percent !== original.progress_percent) {
    patch.progress_percent = d.progress_percent;
  }
  const dateOrNull = (s: string): string | null => (s === '' ? null : s);
  if (dateOrNull(d.schedule_start) !== original.schedule_start) {
    patch.schedule_start = dateOrNull(d.schedule_start);
  }
  if (dateOrNull(d.schedule_end) !== original.schedule_end) {
    patch.schedule_end = dateOrNull(d.schedule_end);
  }
  if (dateOrNull(d.internal_start) !== original.internal_start) {
    patch.internal_start = dateOrNull(d.internal_start);
  }
  if (dateOrNull(d.internal_end) !== original.internal_end) {
    patch.internal_end = dateOrNull(d.internal_end);
  }
  if (d.site_manager_id !== original.site_manager_id) {
    patch.site_manager_id = d.site_manager_id;
  }
  if (trim(d.subcontractor) !== original.subcontractor) {
    patch.subcontractor = trim(d.subcontractor);
  }
  if (trim(d.notes) !== original.notes) {
    patch.notes = trim(d.notes);
  }
  return patch;
}

export function TaskEditModal({ task, onClose, onSave, onDelete, saving, readOnly = false }: TaskEditModalProps) {
  const users = useUsersLookup();
  const [draft, setDraft] = useState<Draft>(emptyDraft());

  useEffect(() => {
    if (task) setDraft(taskToDraft(task));
  }, [task]);

  if (!task) return null;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const patch = draftToPatch(draft, task);
    onSave(patch);
  };

  const canDelete = !readOnly && !!onDelete;

  return (
    <Modal
      open={!!task}
      title={
        readOnly
          ? task.is_group_header
            ? 'Gruppe'
            : `Aufgabe ${task.position_number}`
          : task.is_group_header
            ? 'Gruppe bearbeiten'
            : `Aufgabe ${task.position_number}`
      }
      onClose={onClose}
      width={560}
      footer={
        <>
          {canDelete && onDelete && (
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => onDelete(task.id)}
              style={{ marginRight: 'auto' }}
            >
              Löschen
            </button>
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {readOnly ? 'Schliessen' : 'Abbrechen'}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving || !draft.title.trim()}
            >
              {saving ? 'Speichere …' : 'Speichern'}
            </button>
          )}
        </>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <div className="form-grid">
          <div className="form-group full">
            <label className="form-label">Bezeichnung *</label>
            <input
              value={draft.title}
              onChange={(e) => set('title', e.target.value)}
              autoFocus
              disabled={readOnly}
            />
          </div>

          {!task.is_group_header && (
            <>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  value={draft.status}
                  data-status={draft.status}
                  onChange={(e) => set('status', e.target.value as TaskStatus)}
                  disabled={readOnly}
                >
                  {TASK_STATUS.map((s) => (
                    <option key={s} value={s}>
                      {TASK_STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Fortschritt (%)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={draft.progress_percent}
                  onChange={(e) =>
                    set('progress_percent', Math.max(0, Math.min(100, Number(e.target.value) || 0)))
                  }
                  disabled={readOnly}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Bauzeit von</label>
                <DateInputWithClear
                  value={draft.schedule_start}
                  onChange={(v) => set('schedule_start', v)}
                  disabled={readOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Bauzeit bis</label>
                <DateInputWithClear
                  value={draft.schedule_end}
                  onChange={(v) => set('schedule_end', v)}
                  disabled={readOnly}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Unser Termin von</label>
                <DateInputWithClear
                  value={draft.internal_start}
                  onChange={(v) => set('internal_start', v)}
                  disabled={readOnly}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Unser Termin bis</label>
                <DateInputWithClear
                  value={draft.internal_end}
                  onChange={(v) => set('internal_end', v)}
                  disabled={readOnly}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Bauleiter</label>
                <select
                  value={draft.site_manager_id ?? ''}
                  onChange={(e) =>
                    set('site_manager_id', e.target.value ? Number(e.target.value) : null)
                  }
                  disabled={readOnly}
                >
                  <option value="">—</option>
                  {users.data?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.abbreviation})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Subunternehmer</label>
                <input
                  value={draft.subcontractor}
                  onChange={(e) => set('subcontractor', e.target.value)}
                  disabled={readOnly}
                />
              </div>

              <div className="form-group full">
                <label className="form-label">Bemerkungen</label>
                <textarea
                  value={draft.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                  disabled={readOnly}
                />
              </div>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
}

/**
 * Datums-Eingabe mit "×"-Knopf zum Entfernen. iOS Safari & viele
 * Mobile-Browser zeigen für `<input type="date">` einen Picker an,
 * über den ein einmal gesetztes Datum nicht mehr leer gesetzt werden
 * kann. Der Clear-Button löst genau das.
 */
function DateInputWithClear({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{ flex: 1, minWidth: 0 }}
      />
      {value && !disabled && (
        <button
          type="button"
          onClick={() => onChange('')}
          title="Datum entfernen"
          aria-label="Datum entfernen"
          style={{
            background: 'transparent',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            color: 'var(--color-text-soft)',
            cursor: 'pointer',
            padding: '0 10px',
            height: 36,
            fontSize: 16,
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      )}
    </div>
  );
}
